import { useEffect, useState } from 'react';

import { ApiError, ensureSession } from './api/client';
import { AdminPage } from './pages/AdminPage';
import { BonusPage } from './pages/BonusPage';
import { ClubPage } from './pages/ClubPage';
import { HistoryPage } from './pages/HistoryPage';
import { SettingsPage } from './pages/SettingsPage';
import { EventDetailPage } from './pages/EventDetailPage';
import { EventsPage } from './pages/EventsPage';
import { HomePage } from './pages/HomePage';
import { PeoplePage } from './pages/PeoplePage';
import { PersonPage } from './pages/PersonPage';
import { ProfilePage } from './pages/ProfilePage';
import {
  describeLaunch,
  isInAppBrowser,
  isInsideTelegram,
  isTelegramWithoutInitData,
} from './telegram';

// Куда возвращать человека, если он попал сюда по прямой ссылке.
const BOT_URL = `https://t.me/${import.meta.env.VITE_BOT_USERNAME ?? 'MestoSilyBali_bot'}`;

type Tab =
  | 'home'
  | 'events'
  | 'people'
  | 'bonus'
  | 'history'
  | 'profile'
  | 'admin'
  | 'club'
  | 'ops'
  | 'settings';
type View = { kind: 'tab' } | { kind: 'event'; id: number } | { kind: 'person'; id: number };

const TABS: { key: Tab; label: string }[] = [
  { key: 'home', label: 'Главная' },
  { key: 'events', label: 'События' },
  { key: 'people', label: 'Люди' },
  { key: 'bonus', label: 'Бонусы' },
  { key: 'history', label: 'История' },
  { key: 'profile', label: 'Профиль' },
];

// Вкладки админа. Скрыть их — удобство, а не защита: сами данные
// закрыты на бэкенде, и он отдаёт 404, а не 403.
const ADMIN_TABS: { key: Tab; label: string }[] = [
  { key: 'admin', label: 'Статы' },
  { key: 'ops', label: 'Операции' },
  { key: 'club', label: 'Клуб' },
  { key: 'settings', label: 'Настройки' },
];

export default function App() {
  const [tab, setTab] = useState<Tab>('home');
  const [isAdmin, setIsAdmin] = useState(false);
  const [view, setView] = useState<View>({ kind: 'tab' });
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [details, setDetails] = useState<string[]>([]);
  // По прямой ссылке подписи нет и взяться ей неоткуда — перезагрузка
  // не поможет, нужно вернуться в бота.
  const [wrongEntry, setWrongEntry] = useState(false);

  useEffect(() => {
    if (!isInsideTelegram()) {
      setStatus('error');
      setDetails(describeLaunch());
      setWrongEntry(isInAppBrowser());

      if (isInAppBrowser()) {
        setMessage(
          'По прямой ссылке вход не работает: Telegram подписывает только запуск с кнопки бота. Откройте бота и отправьте /app — в ответ придёт кнопка.',
        );
      } else if (isTelegramWithoutInitData()) {
        setMessage(
          'Telegram не передал данные входа. Закройте приложение и откройте заново — командой /app в боте.',
        );
      } else {
        setMessage('Откройте приложение через бота @MestoSilyBali_bot');
      }
      return;
    }
    ensureSession()
      .then(({ user }) => {
        setIsAdmin(user.role === 'admin');
        setStatus('ready');
      })
      .catch((error: unknown) => {
        setStatus('error');
        setDetails(describeLaunch());
        setMessage(
          error instanceof ApiError
            ? `Не удалось войти (${error.status}). Попробуйте открыть приложение заново.`
            : 'Что-то пошло не так.',
        );
      });
  }, []);

  if (status !== 'ready') {
    return (
      <div className="screen screen--center">
        <div>
          <h1 className="hero__title">Место Силы</h1>
          <p className="muted">{status === 'loading' ? 'Входим…' : message}</p>

          {status === 'error' && (
            <>
              {wrongEntry ? (
                <a className="btn" href={BOT_URL}>
                  Открыть бота
                </a>
              ) : (
                <button className="btn" onClick={() => window.location.reload()}>
                  Попробовать снова
                </button>
              )}
              {/* Без этих строк «не работает» невозможно отличить
                  от «открыли не оттуда». */}
              <pre className="diag">{details.join('\n')}</pre>
            </>
          )}
        </div>
      </div>
    );
  }

  const openEvent = (id: number) => setView({ kind: 'event', id });
  const openPerson = (id: number) => setView({ kind: 'person', id });
  const backToTab = () => setView({ kind: 'tab' });

  if (view.kind === 'event') {
    return (
      <div className="app">
        <EventDetailPage
          eventId={view.id}
          onBack={backToTab}
          onOpenPerson={openPerson}
        />
      </div>
    );
  }

  if (view.kind === 'person') {
    return (
      <div className="app">
        <PersonPage userId={view.id} onBack={backToTab} />
      </div>
    );
  }

  return (
    <div className="app">
      {tab === 'home' && (
        <HomePage onOpenEvent={openEvent} onSeeAll={() => setTab('events')} />
      )}
      {tab === 'events' && <EventsPage onOpenEvent={openEvent} />}
      {tab === 'people' && <PeoplePage onOpenPerson={openPerson} />}
      {tab === 'profile' && <ProfilePage onOpenEvent={openEvent} />}
      {tab === 'bonus' && <BonusPage />}
      {tab === 'history' && <HistoryPage />}
      {tab === 'admin' && isAdmin && <AdminPage />}
      {tab === 'club' && isAdmin && <ClubPage />}
      {tab === 'ops' && isAdmin && <HistoryPage community />}
      {tab === 'settings' && isAdmin && <SettingsPage />}

      <nav className={`tabbar tabbar--${isAdmin ? TABS.length + ADMIN_TABS.length : TABS.length}`}>
        {(isAdmin ? [...TABS, ...ADMIN_TABS] : TABS).map((item) => (
          <button
            key={item.key}
            className={`tab ${tab === item.key ? 'tab--active' : ''}`}
            onClick={() => setTab(item.key)}
          >
            {item.label}
          </button>
        ))}
      </nav>
    </div>
  );
}
